/**
 * User Store - Zustand
 *
 * Centralized state management for users
 *
 * Features:
 * - CRUD operations (Create, Read, Update, Delete)
 * - Real-time search and filtering
 * - Pagination support
 * - Sorting capabilities
 * - Loading and error states
 * - Optimistic UI updates
 * - Toast notifications
 * - User status management (active, inactive, suspended)
 * - Password reset functionality
 *
 * Backend API: /api/users
 *
 * @module useUserStore
 */

import { create } from 'zustand'
import { userService } from '@services/userService'
import toast from 'react-hot-toast'

/**
 * User Store
 *
 * State Management for Users
 */
export const useUserStore = create((set, get) => ({
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * List of all users
   * @type {Array<Object>}
   */
  users: [],

  /**
   * Currently selected user for viewing/editing
   * @type {Object|null}
   */
  selectedUser: null,

  /**
   * Loading state for async operations
   * @type {boolean}
   */
  loading: false,

  /**
   * Error message if operation fails
   * @type {string|null}
   */
  error: null,

  /**
   * Filter and search parameters
   * @type {Object}
   */
  filters: {
    search: '',              // Search by name/email
    company_id: 'all',       // Filter by company
    role_id: 'all',          // Filter by role
    status: 'all',           // all, active, inactive, suspended
    sortBy: 'first_name',    // Field to sort by
    sortOrder: 'asc',        // asc or desc
  },

  /**
   * Pagination configuration
   * @type {Object}
   */
  pagination: {
    page: 1,                 // Current page
    limit: 25,               // Items per page
    total: 0,                // Total number of items
    totalPages: 0,           // Total number of pages
  },

  /**
   * Statistics about users
   * @type {Object}
   */
  stats: {
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0,
  },

  /**
   * Companies list for filter dropdown
   * @type {Array<Object>}
   */
  companies: [],

  /**
   * Roles list for filter dropdown
   * @type {Array<Object>}
   */
  roles: [],

  // ============================================================================
  // ACTIONS - READ OPERATIONS
  // ============================================================================

  /**
   * Fetch all users with current filters
   *
   * @returns {Promise<void>}
   */
  fetchUsers: async () => {
    set({ loading: true, error: null })

    try {
      const { filters, pagination } = get()

      // Build query parameters
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      }

      if (filters.search) {
        params.search = filters.search
      }

      if (filters.company_id !== 'all') {
        params.company_id = filters.company_id
      }

      if (filters.role_id !== 'all') {
        params.role_id = filters.role_id
      }

      if (filters.status !== 'all') {
        params.status = filters.status
      }

      params.sort = `${filters.sortBy}:${filters.sortOrder}`

      // Make API request via service
      const response = await userService.getAll(params)

      // Backend returns { status, count, data: [...] } or { status, message, data: [...], meta: {...} }
      const users = response.data || []
      const meta = response.meta || {}
      const total = meta.total || response.count || users.length

      // Update state with response data
      set({
        users: users,
        pagination: {
          ...pagination,
          page: meta.page || pagination.page,
          total: total,
          totalPages: meta.total_pages || Math.ceil(total / pagination.limit),
        },
        loading: false,
      })

      // Update statistics
      get().calculateStats()

    } catch (error) {
      console.error('Failed to fetch users:', error)
      set({
        error: error.message || 'Failed to load users',
        loading: false,
      })
      toast.error('Failed to load users')
    }
  },

  /**
   * Fetch single user by ID
   *
   * @param {number} id - User ID
   * @returns {Promise<Object>} User data
   */
  fetchUserById: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await userService.getById(id)
      const user = response.data || response

      set({
        selectedUser: user,
        loading: false,
      })

      return user

    } catch (error) {
      console.error('Failed to fetch user:', error)
      set({
        error: error.message || 'User not found',
        loading: false,
      })
      toast.error('User not found')
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - CREATE OPERATIONS
  // ============================================================================

  /**
   * Create new user
   *
   * @param {Object} data - User data
   * @param {string} data.first_name - First name
   * @param {string} data.last_name - Last name
   * @param {string} data.email - Email address
   * @param {string} data.password - Password
   * @param {number} data.company_id - Company ID
   * @param {number} data.role_id - Role ID
   * @param {string} data.status - Status (active/inactive/suspended)
   * @returns {Promise<Object>} Created user
   */
  createUser: async (data) => {
    set({ loading: true, error: null })

    try {
      const response = await userService.create(data)
      const newUser = response.data || response

      // Optimistic UI update - add to beginning of list
      set((state) => ({
        users: [newUser, ...state.users],
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
        loading: false,
      }))

      toast.success('User created successfully!')

      // Update statistics
      get().calculateStats()

      return newUser

    } catch (error) {
      console.error('Failed to create user:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to create user'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update existing user
   *
   * @param {number} id - User ID
   * @param {Object} data - Updated user data
   * @returns {Promise<Object>} Updated user
   */
  updateUser: async (id, data) => {
    set({ loading: true, error: null })

    try {
      const response = await userService.update(id, data)
      const updatedUser = response.data || response

      // Optimistic UI update - replace in list
      set((state) => ({
        users: state.users.map((user) =>
          user.id == id ? updatedUser : user
        ),
        selectedUser: state.selectedUser?.id == id
          ? updatedUser
          : state.selectedUser,
        loading: false,
      }))

      toast.success('User updated successfully!')

      // Update statistics
      get().calculateStats()

      return updatedUser

    } catch (error) {
      console.error('Failed to update user:', error)

      // Extract detailed error message
      let errorMessage = 'Failed to update user'

      if (error.response?.data) {
        const responseData = error.response.data

        if (responseData.messages) {
          if (typeof responseData.messages === 'object') {
            const errors = Object.values(responseData.messages).filter(msg => typeof msg === 'string')
            if (errors.length > 0) {
              errorMessage = errors.join(', ')
            }
          } else if (typeof responseData.messages === 'string') {
            errorMessage = responseData.messages
          }
        } else if (responseData.message) {
          errorMessage = responseData.message
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Update user status
   *
   * @param {number} id - User ID
   * @param {string} status - New status (active/inactive/suspended)
   * @returns {Promise<Object>} Updated user
   */
  updateUserStatus: async (id, status) => {
    set({ loading: true, error: null })

    try {
      const response = await userService.updateStatus(id, status)
      const updatedUser = response.data || response

      // Optimistic UI update
      set((state) => ({
        users: state.users.map((user) =>
          user.id == id ? { ...user, status } : user
        ),
        selectedUser: state.selectedUser?.id == id
          ? { ...state.selectedUser, status }
          : state.selectedUser,
        loading: false,
      }))

      toast.success(`User status updated to ${status}`)

      // Update statistics
      get().calculateStats()

      return updatedUser

    } catch (error) {
      console.error('Failed to update user status:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user status'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Reset user password
   *
   * @param {number} id - User ID
   * @param {string} newPassword - New password
   * @returns {Promise<void>}
   */
  resetUserPassword: async (id, newPassword) => {
    set({ loading: true, error: null })

    try {
      await userService.resetPassword(id, newPassword)

      set({ loading: false })

      toast.success('Password reset successfully!')

    } catch (error) {
      console.error('Failed to reset password:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to reset password'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Block user (set status to suspended)
   *
   * @param {number} id - User ID
   * @returns {Promise<Object>} Updated user
   */
  blockUser: async (id) => {
    return await get().updateUserStatus(id, 'suspended')
  },

  /**
   * Unblock user (set status to active)
   *
   * @param {number} id - User ID
   * @returns {Promise<Object>} Updated user
   */
  unblockUser: async (id) => {
    return await get().updateUserStatus(id, 'active')
  },

  // ============================================================================
  // ACTIONS - DELETE OPERATIONS
  // ============================================================================

  /**
   * Delete (soft delete) user
   * Soft delete - preserves data but marks as deleted
   *
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  deleteUser: async (id) => {
    set({ loading: true, error: null })

    try {
      await userService.delete(id)

      // Optimistic UI update - mark as deleted
      set((state) => ({
        users: state.users.map((user) =>
          user.id == id ? { ...user, deleted_at: new Date().toISOString() } : user
        ),
        selectedUser: state.selectedUser?.id == id ? null : state.selectedUser,
        loading: false,
      }))

      toast.success('User deleted successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters (deleted users may be hidden)
      setTimeout(() => get().fetchUsers(), 100)

    } catch (error) {
      console.error('Failed to delete user:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  /**
   * Restore deleted user
   *
   * @param {number} id - User ID
   * @returns {Promise<void>}
   */
  restoreUser: async (id) => {
    set({ loading: true, error: null })

    try {
      const response = await userService.restore(id)
      const restored = response.data || response

      // Optimistic UI update - use actual restored data from backend
      set((state) => ({
        users: state.users.map((user) =>
          user.id == id ? restored : user
        ),
        selectedUser: state.selectedUser?.id == id ? null : state.selectedUser,
        loading: false,
      }))

      toast.success('User restored successfully!')

      // Update statistics
      get().calculateStats()

      // Refresh to apply filters
      setTimeout(() => get().fetchUsers(), 100)

    } catch (error) {
      console.error('Failed to restore user:', error)

      const errorMessage = error.response?.data?.message || error.message || 'Failed to restore user'

      set({
        error: errorMessage,
        loading: false,
      })

      toast.error(errorMessage)
      throw error
    }
  },

  // ============================================================================
  // ACTIONS - FILTER & SEARCH
  // ============================================================================

  /**
   * Update search/filter parameters and refresh data
   *
   * @param {Object} newFilters - New filter values
   */
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 }, // Reset to page 1 on filter change
    }))

    // Fetch with new filters
    get().fetchUsers()
  },

  /**
   * Update search term
   *
   * @param {string} searchTerm - Search query
   */
  setSearch: (searchTerm) => {
    get().setFilters({ search: searchTerm })
  },

  /**
   * Update company filter
   *
   * @param {string|number} companyId - Company ID (or 'all')
   */
  setCompanyFilter: (companyId) => {
    get().setFilters({ company_id: companyId })
  },

  /**
   * Update role filter
   *
   * @param {string|number} roleId - Role ID (or 'all')
   */
  setRoleFilter: (roleId) => {
    get().setFilters({ role_id: roleId })
  },

  /**
   * Update status filter
   *
   * @param {string} status - Status filter (all, active, inactive, suspended)
   */
  setStatusFilter: (status) => {
    get().setFilters({ status })
  },

  /**
   * Update sorting
   *
   * @param {string} sortBy - Field to sort by
   * @param {string} sortOrder - Sort order (asc/desc)
   */
  setSorting: (sortBy, sortOrder = 'asc') => {
    get().setFilters({ sortBy, sortOrder })
  },

  /**
   * Clear all filters
   */
  clearFilters: () => {
    set({
      filters: {
        search: '',
        company_id: 'all',
        role_id: 'all',
        status: 'all',
        sortBy: 'first_name',
        sortOrder: 'asc',
      },
      pagination: {
        ...get().pagination,
        page: 1,
      },
    })

    get().fetchUsers()
  },

  // ============================================================================
  // ACTIONS - PAGINATION
  // ============================================================================

  /**
   * Go to specific page
   *
   * @param {number} page - Page number
   */
  setPage: (page) => {
    set((state) => ({
      pagination: { ...state.pagination, page },
    }))

    get().fetchUsers()
  },

  /**
   * Change items per page
   *
   * @param {number} limit - Items per page
   */
  setPageLimit: (limit) => {
    set((state) => ({
      pagination: { ...state.pagination, limit, page: 1 },
    }))

    get().fetchUsers()
  },

  /**
   * Go to next page
   */
  nextPage: () => {
    const { pagination } = get()
    if (pagination.page < pagination.totalPages) {
      get().setPage(pagination.page + 1)
    }
  },

  /**
   * Go to previous page
   */
  prevPage: () => {
    const { pagination } = get()
    if (pagination.page > 1) {
      get().setPage(pagination.page - 1)
    }
  },

  // ============================================================================
  // ACTIONS - UTILITIES
  // ============================================================================

  /**
   * Calculate statistics from current users
   */
  calculateStats: () => {
    const { users } = get()

    const stats = {
      total: users.length,
      active: users.filter((user) => user.status === 'active').length,
      inactive: users.filter((user) => user.status === 'inactive').length,
      suspended: users.filter((user) => user.status === 'suspended').length,
    }

    set({ stats })
  },

  /**
   * Select user for viewing/editing
   *
   * @param {Object|null} user - User to select
   */
  selectUser: (user) => {
    set({ selectedUser: user })
  },

  /**
   * Clear selected user
   */
  clearSelection: () => {
    set({ selectedUser: null })
  },

  /**
   * Fetch companies for filter dropdown
   *
   * @returns {Promise<void>}
   */
  fetchCompanies: async () => {
    try {
      const response = await userService.getCompanies()
      const companies = response.data || []

      set({ companies })

    } catch (error) {
      console.error('Failed to fetch companies:', error)
      toast.error('Failed to load companies')
    }
  },

  /**
   * Fetch roles for filter dropdown
   *
   * @returns {Promise<void>}
   */
  fetchRoles: async () => {
    try {
      const response = await userService.getRoles()
      const roles = response.data || []

      set({ roles })

    } catch (error) {
      console.error('Failed to fetch roles:', error)
      toast.error('Failed to load roles')
    }
  },

  /**
   * Reset entire store to initial state
   */
  reset: () => {
    set({
      users: [],
      selectedUser: null,
      loading: false,
      error: null,
      filters: {
        search: '',
        company_id: 'all',
        role_id: 'all',
        status: 'all',
        sortBy: 'first_name',
        sortOrder: 'asc',
      },
      pagination: {
        page: 1,
        limit: 25,
        total: 0,
        totalPages: 0,
      },
      stats: {
        total: 0,
        active: 0,
        inactive: 0,
        suspended: 0,
      },
      companies: [],
      roles: [],
    })
  },
}))

export default useUserStore
